"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { SiCashapp } from "react-icons/si";
import { Zap } from "lucide-react";
import StyledQR from "./StyledQR";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";

type OrderStatus =
  | "NEW"
  | "PENDING"
  | "EXCHANGE"
  | "DONE"
  | "EXPIRED"
  | "EMERGENCY";

interface LightningOrder {
  orderId: string;
  token: string;
  invoice: string;
  btcAmount: string;
  usdcAmount: string;
  expiresAt: number;
  status: OrderStatus;
}

const STEPS: {
  key: OrderStatus;
  label: string;
  sub: string;
  icon: React.ReactNode;
}[] = [
  {
    key: "NEW",
    label: "Awaiting Payment",
    sub: "Send BTC via Lightning",
    icon: (
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    key: "PENDING",
    label: "Payment Received",
    sub: "Confirming on network",
    icon: (
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    key: "EXCHANGE",
    label: "Converting to USDC",
    sub: "Sending to your wallet",
    icon: (
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
        />
      </svg>
    ),
  },
  {
    key: "DONE",
    label: "Complete",
    sub: "Funds in your wallet",
    icon: (
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
];

const CASHAPP_HOW_TO_STEPS = [
  {
    num: "1",
    text: "Open CashApp and tap the QR icon in the top-right corner",
  },
  {
    num: "2",
    text: 'Point your camera at the QR code — or tap "Pay with Cash App" to open directly',
  },
  { num: "3", text: "Confirm the payment amount and send" },
];

const LIGHTNING_HOW_TO_STEPS = [
  {
    num: "1",
    text: "Open your Lightning wallet (Strike, Phoenix, Muun, or any Lightning app)",
  },
  {
    num: "2",
    text: 'Scan the QR code — or tap "Pay via Lightning" to open your wallet directly',
  },
  { num: "3", text: "Confirm the payment amount and send" },
];

function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-gray-800/50 ${className}`}
    >
      <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/4 to-transparent animate-[shimmer_1.4s_ease-in-out_infinite]" />
    </div>
  );
}

function formatUsdcAmount(amount: string | number | undefined): string {
  if (!amount) return "0";
  const num = parseFloat(String(amount));
  if (isNaN(num)) return "0";
  // Remove trailing zeros and unnecessary decimal point
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export default function LightningPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId: urlOrderId } = useParams<{ orderId?: string }>();
  const state = location.state as {
    amount: number;
    toAddress: string;
    // "ff"  → pin to FixedFloat (no SimpleSwap fallback). Surfaces the
    //         "Powered by ff.io" badge on this page.
    // undef → default FF-primary-with-SimpleSwap-fallback behaviour.
    forceProvider?: "ff";
    method?: "lightning-btc" | "lightning-cashapp";
  } | null;

  const [order, setOrder] = useState<LightningOrder | null>(null);
  const [creating, setCreating] = useState(true);
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether this order was created via the FF-only path. Stored
  // separately from `order` because (a) it's a UI hint, not part of
  // the BE response shape, and (b) we want the "Powered by ff.io"
  // badge to render consistently across hot reloads — so we persist
  // it on the Firestore doc and read it back on reload.
  const [forcedFf, setForcedFf] = useState(false);

  // true when the user came from the "Add Money via Lightning" tile.
  // Drives: no CashApp logo in QR, "Pay via Lightning" CTA instead of
  // "Pay with Cash App", and Lightning-specific how-to steps.
  const isLightningBtc =
    state?.method === "lightning-btc" ||
    state?.forceProvider === "ff" ||
    forcedFf;
  // Buyer must explicitly acknowledge the "transfers are irreversible"
  // disclaimer before the "Pay with Cash App" CTA becomes interactive.
  // This is a regulatory/scam-prevention requirement that mirrors the
  // wording used on other parts of the platform — it stops a buyer
  // tapping through reflexively when they've been social-engineered
  // into making a payment.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const firestoreId = useRef<string | null>(null);

  // Auto-scroll to top on mount.
  // Layout's <main> is the real scrollable container (overflow-y-auto), not window.
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Guard: redirect if no state and no urlOrderId
  useEffect(() => {
    if (!state?.amount && !urlOrderId) {
      navigate("/add-funds", { replace: true });
    }
  }, [state, urlOrderId, navigate]);

  const loadOrderFromFirestore = useCallback(async (id: string) => {
    setCreating(true);
    try {
      const q = query(
        collection(db, "lightningOrders"),
        where("orderId", "==", id),
      );
      const snap = await getDocs(q);
      if (snap.empty) throw new Error("Order not found");
      const data = snap.docs[0].data();
      firestoreId.current = snap.docs[0].id;
      const loaded: LightningOrder = {
        orderId: data.orderId,
        token: data.token,
        invoice: data.invoice,
        btcAmount: data.btcAmount,
        usdcAmount: data.usdcAmount,
        expiresAt: data.expiresAt,
        status: data.status,
      };
      setOrder(loaded);
      // Re-hydrate the "Powered by ff.io" badge state when the buyer
      // refreshes the page. We persisted `forceProvider` at create
      // time, so any order with forceProvider === "ff" lights up the
      // badge again here.
      if (data.forceProvider === "ff") setForcedFf(true);
    } catch (e: any) {
      setError(e.message || "Could not load order");
    } finally {
      setCreating(false);
    }
  }, []);

  const createOrder = useCallback(async () => {
    if (!state?.amount || !state?.toAddress) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SERVERURL}/lightning/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({
            amount: state.amount,
            toAddress: state.toAddress,
            // Forward the FF-only pin if the buyer came from the
            // "Add Money via Lightning" tile. The backend skips its
            // SimpleSwap fallback and errors out cleanly if FF is down.
            forceProvider: state.forceProvider,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create invoice");

      const o = data as LightningOrder;
      setOrder(o);
      // Lock in the badge state alongside the order so the UI doesn't
      // flicker between "no badge" and "Powered by ff.io" as state
      // hydrates.
      if (state.forceProvider === "ff") setForcedFf(true);
      navigate(`/add-funds/lightning/${o.orderId}`, { replace: true, state });

      const docRef = await addDoc(collection(db, "lightningOrders"), {
        walletAddress: state.toAddress.toLowerCase(),
        orderId: o.orderId,
        token: o.token,
        invoice: o.invoice,
        btcAmount: o.btcAmount,
        usdcAmount: o.usdcAmount,
        usdAmount: state.amount,
        expiresAt: o.expiresAt,
        status: "NEW",
        createdAt: serverTimestamp(),
        // Persist the FF-only intent so a page reload (loadOrderFromFirestore)
        // can re-render the "Powered by ff.io" badge.
        forceProvider: state.forceProvider || null,
      });
      firestoreId.current = docRef.id;
    } catch (e: any) {
      setError(e.message || "Could not create invoice");
    } finally {
      setCreating(false);
    }
  }, [state, navigate]);

  useEffect(() => {
    // If an orderId is in the URL, always load from Firestore — never hit the API again.
    // This covers both direct URL access and page reloads after initial creation.
    if (urlOrderId) {
      loadOrderFromFirestore(urlOrderId);
    } else if (state?.amount) {
      createOrder();
    }
    // If neither, the guard effect above already redirects to /add-funds
  }, []);

  // Countdown
  useEffect(() => {
    if (!order?.expiresAt) return;
    const tick = () =>
      setSecondsLeft(
        Math.max(0, order.expiresAt - Math.floor(Date.now() / 1000)),
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [order?.expiresAt]);

  // Poll status every 5s
  useEffect(() => {
    if (!order || ["DONE", "EXPIRED", "EMERGENCY"].includes(order.status))
      return;
    const poll = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SERVERURL}/lightning/status/${order.orderId}?token=${encodeURIComponent(order.token)}`,
          {
            headers: {
              Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
            },
          },
        );
        const data = await res.json();
        if (data.status && data.status !== order.status) {
          const s = data.status as OrderStatus;
          setOrder((p) => (p ? { ...p, status: s } : p));
          if (firestoreId.current)
            updateDoc(doc(db, "lightningOrders", firestoreId.current), {
              status: s,
            }).catch(() => {});
          if (s === "DONE")
            toast.success("Payment complete! Funds added to your wallet.");
          if (s === "EXPIRED") toast.error("Invoice expired.");
        }
      } catch {}
    };
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [order]);

  const copy = () => {
    if (!order?.invoice) return;
    navigator.clipboard.writeText(order.invoice);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fmt = (s: number) =>
    `${Math.floor(s / 60)
      .toString()
      .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const stepIdx = order ? STEPS.findIndex((s) => s.key === order.status) : -1;
  const isDone = order?.status === "DONE";
  const isExpired =
    order?.status === "EXPIRED" ||
    (order?.status === "NEW" && secondsLeft === 0 && !creating);
  const isEmergency = order?.status === "EMERGENCY";

  // Progress percentage for the bar
  const progressPct = creating
    ? 0
    : isDone
      ? 100
      : stepIdx >= 0
        ? Math.round(((stepIdx + 0.5) / STEPS.length) * 100)
        : 0;

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-80px] left-1/2 -translate-x-1/2 w-[400px] h-[220px] rounded-full bg-[#61CE70]/6 blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[250px] h-[200px] rounded-full bg-emerald-500/4 blur-[100px]" />
      </div>

      <div className="relative max-w-5xl mx-auto px-2 min-[400px]:px-3 py-3 min-[400px]:py-4 sm:px-6 sm:py-10">
        {/* Header bar — back button hidden on mobile */}
        <div className="flex items-center justify-between mb-4 sm:mb-8">
          <button
            onClick={() => navigate("/add-funds")}
            className="hidden sm:flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors duration-150 group"
            aria-label="Back to Add Funds"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-gray-800/60 group-hover:bg-gray-700/60 transition-colors border border-gray-700/50">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </span>
            <span className="hidden sm:inline font-medium">Add Funds</span>
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            {order && !creating && (
              <span className="text-[9px] sm:text-[10px] font-mono text-gray-600 hidden sm:inline">
                #{order.orderId}
              </span>
            )}
          </div>
        </div>

        {/* Page title — Lightning-branded.
            Important: we deliberately do NOT use the Cash App name or
            logo here. Our Lightning rail (via the FF / SimpleSwap
            providers) doesn't license the Cash App brand and using
            their mark on a hosted payment page is a TOS violation. The
            on-page CTA still says "Pay with Cash App" because that's
            literally where the buyer is going to tap-to-launch, but
            the page chrome stays generic-Lightning so we're never
            *representing* this page as Cash App's. */}
        <div className="mb-3 md:mt-0 -mt-7 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            {/* Concentric-ring Lightning glyph — same visual language as
                the "Add Money via Lightning" tile on the Add Funds page
                so the buyer recognises the rail. Decorative; the heading
                below carries the meaning. */}
            {isLightningBtc ? (
              <div className="relative w-8 h-8 min-[400px]:w-9 min-[400px]:h-9 sm:w-10 sm:h-10 shrink-0 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full border border-dashed border-[#61CE70]/35 animate-[ping_2.6s_cubic-bezier(0,0,0.2,1)_infinite]" />
                <span className="absolute inset-[3px] rounded-full border border-dashed border-[#61CE70]/55" />
                <span
                  className="relative w-5 h-5 min-[400px]:w-5.5 min-[400px]:h-5.5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #61CE70 0%, #3FA653 100%)",
                    boxShadow:
                      "0 0 14px rgba(97,206,112,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                  }}
                >
                  <Zap
                    className="w-3 h-3 min-[400px]:w-3.5 min-[400px]:h-3.5 sm:w-4 sm:h-4 text-white"
                    strokeWidth={2.6}
                    fill="white"
                  />
                </span>
              </div>
            ) : (
              <SiCashapp className="w-8 h-8 min-[400px]:w-9 min-[400px]:h-9 sm:w-10 sm:h-10 shrink-0 text-[#00D632]" />
            )}
            <h1 className="text-lg min-[400px]:text-xl sm:text-2xl lg:text-3xl font-bold text-white tracking-tight leading-tight">
              Add Money{" "}
              <span className="text-[#61CE70]">
                {isLightningBtc ? "via Lightning" : "via Cashapp Pay"}
              </span>
            </h1>
          </div>
          <p className="text-gray-500 md:block hidden text-[11px] min-[400px]:text-xs sm:text-sm ml-[40px] min-[400px]:ml-[44px] sm:ml-[52px]">
            {isLightningBtc
              ? "Instant Payment using Lightning Invoice"
              : "Instant Payment using CashApp Pay"}
          </p>
          {/* "Powered by ff.io" attribution badge. Renders only for
              orders that came through the FF-only path (Add Money via
              Lightning tile on the Add Funds page). The badge sits
              indented to align with the subtitle on desktop and on its
              own line on mobile so it never crowds the heading. */}
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-2 min-[400px]:gap-3 sm:gap-4 lg:gap-6 items-start min-w-0">
          {/* ─── LEFT: Amount + QR + Invoice + CTA ─── */}
          <div className="space-y-2 min-[400px]:space-y-3 sm:space-y-4">
            {/* Amount row */}
            <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl p-3 min-[400px]:p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[9px] min-[400px]:text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                    You receive
                  </p>
                  {creating ? (
                    <Shimmer className="h-7 min-[400px]:h-8 w-24 min-[400px]:w-28 sm:w-32" />
                  ) : (
                    <p className="text-xl min-[400px]:text-2xl sm:text-3xl lg:text-4xl font-black text-white tabular-nums leading-none">
                      ${formatUsdcAmount(state?.amount ?? order?.usdcAmount)}
                      <span className="text-[#61CE70] text-xs min-[400px]:text-sm sm:text-base lg:text-lg font-semibold ml-1">
                        USDC
                      </span>
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[9px] min-[400px]:text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                    You pay
                  </p>
                  {creating ? (
                    <Shimmer className="h-5 w-20 min-[400px]:w-24 ml-auto" />
                  ) : (
                    <p className="text-xs min-[400px]:text-sm sm:text-base font-bold text-white tabular-nums font-mono">
                      {order?.btcAmount}
                      <span className="text-amber-400 text-[10px] min-[400px]:text-xs sm:text-sm ml-1">
                        BTC
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* QR card */}
            <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl overflow-hidden">
              {/* Timer strip */}
              {!creating && order && !isDone && !isEmergency && (
                <div
                  className={`flex items-center justify-between px-4 sm:px-5 py-2.5 border-b text-xs font-medium ${
                    isExpired
                      ? "border-red-500/20 bg-red-500/5 text-red-400"
                      : secondsLeft < 120
                        ? "border-amber-500/20 bg-amber-500/5 text-amber-400"
                        : "border-gray-800/60 text-gray-500"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-3.5 h-3.5 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path strokeLinecap="round" d="M12 6v6l4 2" />
                    </svg>
                    {isExpired
                      ? "Invoice expired"
                      : `Expires in ${fmt(secondsLeft)}`}
                  </div>
                  <span className="text-gray-600 text-[10px] font-normal">
                    Est. 2–5 min
                  </span>
                </div>
              )}

              <div className="p-3 min-[400px]:p-4 sm:p-6 lg:p-8 flex flex-col items-center gap-3 min-[400px]:gap-4 sm:gap-6">
                {/* LOADING */}
                {creating && (
                  <div className="flex flex-col items-center gap-4 w-full py-2">
                    <div className="relative w-[155px] h-[155px] min-[400px]:w-[185px] min-[400px]:h-[185px] sm:w-[220px] sm:h-[220px] bg-white rounded-2xl flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 animate-pulse" />
                      <div className="relative z-10 flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-[3px] border-[#61CE70]/30 border-t-[#61CE70] rounded-full animate-spin" />
                        <p className="text-xs text-gray-400 font-medium">
                          Generating…
                        </p>
                      </div>
                    </div>
                    <div className="w-full space-y-2">
                      <Shimmer className="h-11 w-full" />
                      <Shimmer className="h-13 w-full" />
                    </div>
                  </div>
                )}

                {/* ERROR */}
                {!creating && error && (
                  <div className="flex flex-col items-center gap-4 py-6 text-center w-full">
                    <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                      <svg
                        className="w-7 h-7 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-red-400 font-semibold">
                        Could not generate invoice
                      </p>
                      <p className="text-gray-500 text-xs mt-1">{error}</p>
                    </div>
                    <button
                      onClick={createOrder}
                      className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl text-sm font-medium transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* ACTIVE INVOICE */}
                {!creating && order && !isDone && !isEmergency && (
                  <>
                    {/* For lightning-btc: show the consent checkbox FIRST so
                        the buyer must agree before the QR is revealed. */}
                    {isLightningBtc && !isExpired && (
                      <label className="flex items-start gap-2.5 cursor-pointer select-none px-1">
                        <input
                          type="checkbox"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                          aria-describedby="cashapp-irreversible-disclaimer"
                          className="mt-0.5 w-4 h-4 rounded border border-gray-600 bg-gray-900/60 text-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/60 focus:ring-offset-0 accent-[#61CE70] shrink-0 cursor-pointer"
                        />
                        <span
                          id="cashapp-irreversible-disclaimer"
                          className="text-[11px] sm:text-xs text-gray-400 leading-relaxed"
                        >
                          By continuing, you confirm this transfer is authorized
                          by you and is final.{" "}
                          <strong className="text-white">
                            Transfers are irreversible.
                          </strong>{" "}
                          CashTap does not hold, cancel, or reverse completed
                          transfers. Beware of scams.
                        </span>
                      </label>
                    )}

                    {/* QR code with CashTap overlay */}
                    <div
                      className={`relative transition-all duration-300 ${isExpired ? "opacity-20 saturate-0" : ""}`}
                    >
                      {/* Green glow halo behind the QR card */}
                      {!isExpired && (
                        <div
                          className="absolute inset-0 rounded-2xl blur-xl opacity-40"
                          style={{
                            background: "rgba(97,206,112,0.35)",
                            transform: "scale(0.9) translateY(4px)",
                          }}
                        />
                      )}
                      <div
                        className="relative p-2 min-[400px]:p-3 sm:p-3.5 bg-white rounded-2xl"
                        style={{
                          boxShadow: isExpired
                            ? "0 8px 32px rgba(0,0,0,0.5)"
                            : "0 0 0 1.5px rgba(97,206,112,0.35), 0 8px 32px rgba(0,0,0,0.5), 0 0 24px rgba(97,206,112,0.12)",
                        }}
                      >
                        {isLightningBtc && !agreedToTerms ? (
                          <div className="w-[155px] h-[155px] min-[400px]:w-[185px] min-[400px]:h-[185px] sm:w-[220px] sm:h-[220px] flex flex-col items-center justify-center gap-2 bg-gray-100 rounded-xl">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                            <p className="text-[11px] text-gray-400 text-center px-4 leading-snug">
                              Confirm above to reveal QR code
                            </p>
                          </div>
                        ) : order?.invoice ? (
                          <div className="relative w-[155px] h-[155px] min-[400px]:w-[185px] min-[400px]:h-[185px] sm:w-[220px] sm:h-[220px]">
                            <StyledQR
                              value={`LIGHTNING:${order.invoice.toUpperCase()}`}
                              className="w-full h-full"
                              logo={isLightningBtc ? "none" : "cashapp"}
                            />
                          </div>
                        ) : (
                          <div className="w-[155px] h-[155px] min-[400px]:w-[185px] min-[400px]:h-[185px] sm:w-[220px] sm:h-[220px] bg-gray-100 animate-pulse rounded-xl" />
                        )}
                      </div>
                      {!isExpired && (
                        <div
                          className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-[#0F1419] border border-[#61CE70]/20 rounded-full text-[10px] whitespace-nowrap"
                          style={{ color: "rgba(97,206,112,0.7)" }}
                        >
                          Lightning Network
                        </div>
                      )}
                    </div>

                    {/* Invoice — compact copy button on mobile, full string on desktop */}
                    <div className="w-full">
                      {/* Show helpful guidance when checkbox unchecked; show invoice copy when checked */}
                      {isLightningBtc && !agreedToTerms ? (
                        <div className="mt-2 p-3.5 sm:p-4 rounded-xl border border-blue-500/30 bg-blue-500/5 flex items-start gap-3">
                          <svg
                            className="w-5 h-5 text-blue-400 shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm text-gray-300 font-medium leading-snug">
                              Once you confirm the transaction is irreversible, scan the QR code with your Lightning wallet to pay.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Mobile: single compact copy button */}
                          <button
                            onClick={copy}
                            disabled={!!isExpired}
                            aria-label={
                              copied ? "Copied" : "Copy Lightning Invoice"
                            }
                            className={`sm:hidden w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-150 active:scale-[0.98]
                              ${
                                isExpired
                                  ? "border-gray-800/40 text-gray-600 opacity-40 cursor-not-allowed"
                                  : copied
                                    ? "border-[#61CE70]/40 bg-[#61CE70]/8 text-[#61CE70]"
                                    : "border-gray-700/50 bg-gray-900/60 text-gray-300 hover:border-[#61CE70]/30 hover:text-white"
                              }`}
                          >
                            {copied ? (
                              <>
                                <svg
                                  className="w-3.5 h-3.5 shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-3.5 h-3.5 shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                >
                                  <rect x="9" y="9" width="13" height="13" rx="2" />
                                  <path
                                    strokeLinecap="round"
                                    d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                                  />
                                </svg>
                                Copy Invoice
                              </>
                            )}
                          </button>

                          {/* Desktop: full invoice string display */}
                          <div className="hidden sm:block">
                            <p className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold mb-1.5 text-center">
                              Lightning Invoice
                            </p>
                            <button
                              onClick={copy}
                              disabled={!!isExpired}
                              aria-label={copied ? "Copied" : "Copy invoice"}
                              className={`w-full min-w-0 flex items-start gap-2 bg-gray-900/60 border rounded-xl px-3.5 py-2.5 text-left transition-all duration-150 group
                                ${isExpired ? "border-gray-800/40 opacity-40 cursor-not-allowed" : "border-gray-700/50 hover:border-[#61CE70]/30 hover:bg-gray-900/80 cursor-pointer"}`}
                            >
                              <span className="text-[10px] line-clamp-3 font-mono text-gray-400 flex-1 min-w-0 break-all leading-relaxed">
                                {order.invoice}
                              </span>
                              <span className="shrink-0 mt-0.5 transition-colors">
                                {copied ? (
                                  <svg
                                    className="w-4 h-4 text-[#61CE70]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2.5}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-4 h-4 text-gray-600 group-hover:text-gray-300"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                  >
                                    <rect
                                      x="9"
                                      y="9"
                                      width="13"
                                      height="13"
                                      rx="2"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                                    />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Consent checkbox for cashapp variant — for lightning-btc
                        this is rendered above the QR so the buyer sees it first. */}
                    {!isExpired && !isLightningBtc && (
                      <label className="flex items-start gap-2.5 cursor-pointer select-none px-1">
                        <input
                          type="checkbox"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                          aria-describedby="cashapp-irreversible-disclaimer"
                          className="mt-0.5 w-4 h-4 rounded border border-gray-600 bg-gray-900/60 text-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/60 focus:ring-offset-0 accent-[#61CE70] shrink-0 cursor-pointer"
                        />
                        <span
                          id="cashapp-irreversible-disclaimer"
                          className="text-[11px] sm:text-xs text-gray-400 leading-relaxed"
                        >
                          By continuing, you confirm this transfer is authorized
                          by you and is final.{" "}
                          <strong className="text-white">
                            Transfers are irreversible.
                          </strong>{" "}
                          CashTap does not hold, cancel, or reverse completed
                          transfers. Beware of scams.
                        </span>
                      </label>
                    )}

                    {/* CTA — hidden for lightning-btc (user scans QR directly) */}
                    {isExpired ? (
                      <button
                        onClick={createOrder}
                        className="w-full py-3 min-[400px]:py-3.5 sm:py-4 rounded-2xl bg-gray-800 hover:bg-gray-700 border border-gray-700/60 text-white font-semibold text-sm transition-all duration-150 active:scale-[0.98]"
                      >
                        Generate New Invoice
                      </button>
                    ) : !isLightningBtc ? (
                      <a
                        href={
                          agreedToTerms
                            ? `https://cash.app/launch/lightning/${order.invoice}`
                            : undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (!agreedToTerms) {
                            e.preventDefault();
                            toast.error(
                              "Please confirm the disclaimer to continue",
                            );
                          }
                        }}
                        aria-disabled={!agreedToTerms}
                        className={`w-full flex items-center justify-center gap-2 sm:gap-3 py-3 min-[400px]:py-3.5 sm:py-4 rounded-2xl font-bold text-sm sm:text-base transition-all duration-150 ${
                          agreedToTerms
                            ? "active:scale-[0.98] cursor-pointer"
                            : "cursor-not-allowed"
                        }`}
                        style={{
                          background: "#00D632",
                          color: "#ffffff",
                          boxShadow: agreedToTerms
                            ? "0 8px 32px rgba(0,214,50,0.22)"
                            : "none",
                          opacity: agreedToTerms ? 1 : 0.45,
                        }}
                        aria-label="Pay with CashApp"
                      >
                        <SiCashapp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        <span className="text-white">Pay with Cash App</span>
                      </a>
                    ) : null}
                    {forcedFf && (
                      // Anchor to ff.io so the attribution reads as a
                      // real outbound link (Visit ff.io). The slow
                      // scale-up on hover (5s) is intentional — it
                      // gently signals "this is clickable" without
                      // making the badge dance the moment a cursor
                      // grazes it. Origin-bottom keeps the bottom edge
                      // anchored as it scales so it doesn't visually
                      // detach from the subtitle above.
                      <a
                        href="https://ff.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Visit ff.io"
                        className="mt-2 ml-[40px] bg-white rounded-lg px-2 min-[400px]:ml-[44px] sm:ml-[52px] inline-flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer transition-transform duration-[5000ms] ease-out hover:scale-110 origin-bottom-left"
                      >
                        <span className="text-xl">Powered by</span>
                        <img
                          src="/ff-logo.png"
                          alt="ff.io"
                          className="h-3.5 sm:h-12 w-auto object-contain opacity-90"
                        />
                      </a>
                    )}

                    {/* Trust badges */}
                    <div className="flex items-center justify-between gap-1.5 pt-0.5">
                      <div className="flex items-center gap-1 flex-1 justify-center">
                        <svg
                          className="w-3 h-3 text-[#61CE70] shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                          Secure Payment
                        </span>
                      </div>
                      <div className="w-px h-3 bg-gray-800" />
                      <div className="flex items-center gap-1 flex-1 justify-center">
                        <svg
                          className="w-3 h-3 text-amber-400 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                          Instant Transfer
                        </span>
                      </div>
                      <div className="w-px h-3 bg-gray-800" />
                      <div className="flex items-center gap-1 flex-1 justify-center">
                        <svg
                          className="w-3 h-3 text-blue-400 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        <span className="text-[9px] min-[400px]:text-[10px] text-gray-400 whitespace-nowrap">
                          Encrypted & Safe
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {/* DONE */}
                {isDone && (
                  <div className="flex flex-col items-center gap-5 py-6 sm:py-8 w-full text-center">
                    <div className="relative w-20 h-20">
                      <div className="absolute inset-0 rounded-full bg-[#61CE70]/20 animate-ping" />
                      <div className="relative w-20 h-20 rounded-full bg-[#61CE70]/15 border border-[#61CE70]/30 flex items-center justify-center">
                        <svg
                          className="w-10 h-10 text-[#61CE70]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-white">
                        Payment Complete
                      </p>
                      <p className="text-gray-400 text-sm mt-1.5">
                        ${formatUsdcAmount(state?.amount ?? order?.usdcAmount)}{" "}
                        USDC added to your wallet
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/wallet")}
                      className="px-7 sm:px-8 py-3.5 rounded-2xl font-bold text-sm transition-all duration-150 active:scale-[0.98]"
                      style={{
                        background: "linear-gradient(135deg, #61CE70, #50B060)",
                        color: "#000",
                      }}
                    >
                      View Wallet
                    </button>
                  </div>
                )}

                {/* EMERGENCY */}
                {isEmergency && (
                  <div className="flex flex-col items-center gap-4 py-6 text-center w-full">
                    <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-red-400 font-semibold">
                      An issue occurred with this order
                    </p>
                    <p className="text-[11px] text-gray-500 font-mono">
                      Order: {order?.orderId}
                    </p>
                    <p className="text-xs text-gray-500 max-w-xs">
                      Contact CashTap support with your Order ID and we'll
                      resolve this promptly.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* ─── RIGHT: Status + Info + Steps ─── */}
          <div className="space-y-2 min-[400px]:space-y-3 sm:space-y-4 lg:sticky lg:top-6">
            {/* ── Payment Status Card ── */}
            <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl overflow-hidden">
              {/* Card header */}
              <div className="flex items-center justify-between px-3 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-4 border-b border-gray-800/60">
                <p className="text-xs font-bold text-white uppercase tracking-widest">
                  Payment Status
                </p>
                {!creating && order && (
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      isDone
                        ? "bg-[#61CE70]/15 text-[#61CE70]"
                        : isExpired
                          ? "bg-red-500/15 text-red-400"
                          : isEmergency
                            ? "bg-red-500/15 text-red-400"
                            : "bg-amber-500/10 text-amber-400"
                    }`}
                  >
                    {isDone
                      ? "Complete"
                      : isExpired
                        ? "Expired"
                        : isEmergency
                          ? "Error"
                          : order.status}
                  </span>
                )}
              </div>

              <div className="px-3 min-[400px]:px-4 sm:px-5 py-3 min-[400px]:py-4 sm:py-5 xs-pad">
                {/* Progress bar */}
                {!creating && (
                  <div className="mb-2 sm:mb-5">
                    <div className="hidden sm:flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                      <span>Progress</span>
                      <span
                        className="font-semibold"
                        style={{
                          color: progressPct === 100 ? "#61CE70" : undefined,
                        }}
                      >
                        {progressPct}%
                      </span>
                    </div>
                    <div className="h-1 sm:h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{
                          width: `${progressPct}%`,
                          background: isExpired
                            ? "#ef4444"
                            : "linear-gradient(90deg, #61CE70, #50B060)",
                          boxShadow:
                            progressPct > 0 && !isExpired
                              ? "0 0 8px rgba(97,206,112,0.5)"
                              : undefined,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Steps */}
                {creating ? (
                  <div className="space-y-3">
                    {STEPS.map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Shimmer className="w-7 h-7 rounded-full shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <Shimmer className="h-3 w-24" />
                          <Shimmer className="h-2.5 w-32" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-0.5 sm:space-y-1">
                    {STEPS.map((step, i) => {
                      const done = stepIdx > i;
                      const active = stepIdx === i;
                      return (
                        <div
                          key={step.key}
                          className={`flex items-center gap-2.5 sm:gap-3 px-2.5 sm:px-3 py-1.5 sm:py-2.5 rounded-xl transition-all duration-300 ${
                            active
                              ? "bg-[#61CE70]/8 border border-[#61CE70]/15"
                              : done
                                ? "border border-transparent"
                                : "border border-transparent opacity-40"
                          }`}
                        >
                          {/* Step icon bubble */}
                          <div className="relative shrink-0">
                            {active && (
                              <span className="absolute inset-0 rounded-full bg-[#61CE70]/25 animate-ping" />
                            )}
                            <div
                              className={`relative w-7 h-7 rounded-full flex items-center justify-center z-10 transition-all duration-500 ${
                                done
                                  ? "bg-[#61CE70]"
                                  : active
                                    ? "bg-[#61CE70]/15 border-2 border-[#61CE70]"
                                    : "bg-gray-800/80 border border-gray-700/60"
                              }`}
                            >
                              {done ? (
                                <svg
                                  className="w-3.5 h-3.5 text-black"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              ) : (
                                <span
                                  className={
                                    active ? "text-[#61CE70]" : "text-gray-600"
                                  }
                                >
                                  {step.icon}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Text */}
                          <div className="flex-1 min-w-0">
                            <p
                              className={`xs-step-text text-xs sm:text-[13px] font-semibold leading-tight ${
                                done
                                  ? "text-[#61CE70]"
                                  : active
                                    ? "text-white"
                                    : "text-gray-500"
                              }`}
                            >
                              {step.label}
                            </p>
                            <p
                              className={`hidden sm:block xs-step-sub text-[10px] sm:text-xs mt-0.5 ${
                                done
                                  ? "text-[#61CE70]/50"
                                  : active
                                    ? "text-gray-400"
                                    : "text-gray-600"
                              }`}
                            >
                              {done ? "Completed" : step.sub}
                            </p>
                          </div>

                          {/* Active indicator */}
                          {active && (
                            <div className="flex items-center gap-0.5 shrink-0">
                              {[0, 100, 200].map((d) => (
                                <span
                                  key={d}
                                  className="w-1 h-1 rounded-full bg-[#61CE70]"
                                  style={{
                                    animation: `bounce 0.8s ease-in-out ${d}ms infinite`,
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Order info — hidden on mobile to preserve 100vh fit */}
            {!creating && order && (
              <div className="hidden sm:block bg-[#0F1419] border border-gray-800/60 rounded-2xl divide-y divide-gray-800/60">
                <div className="flex items-center justify-between px-4 py-3 text-xs">
                  <span className="text-gray-500">Order ID</span>
                  <span className="font-mono text-gray-400">
                    #{order.orderId}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 text-xs">
                  <span className="text-gray-500">Est. time</span>
                  <span className="text-white font-medium">2–5 minutes</span>
                </div>
              </div>
            )}

            {/* How to pay — hidden on mobile to preserve 100vh fit */}
            <div className="hidden sm:block bg-[#0F1419] border border-gray-800/60 rounded-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-gray-800/60">
                <svg
                  className="w-3.5 h-3.5 text-[#61CE70] shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-[11px] font-bold text-white tracking-wide">
                  How to pay
                </p>
              </div>
              {/* Steps */}
              <div className="px-4 sm:px-5 py-4 space-y-0">
                {(isLightningBtc ? LIGHTNING_HOW_TO_STEPS : CASHAPP_HOW_TO_STEPS).map((s, i) => (
                  <div
                    key={s.num}
                    className={`flex items-start gap-3.5 py-3 ${i < (isLightningBtc ? LIGHTNING_HOW_TO_STEPS : CASHAPP_HOW_TO_STEPS).length - 1 ? "border-b border-gray-800/40" : ""}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-[#61CE70]/12 border border-[#61CE70]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span
                        className="text-[10px] font-black leading-none"
                        style={{ color: "#61CE70" }}
                      >
                        {s.num}
                      </span>
                    </div>
                    <p className="xs-howto-text text-[11px] min-[400px]:text-[12px] text-gray-300 leading-[1.5] flex-1 min-w-0 pt-0.5">
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer { 100% { transform: translateX(300%); } }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @media (max-width: 399px) {
          .xs-text-shrink { font-size: 10px !important; }
          .xs-label { font-size: 8px !important; }
          .xs-step-text { font-size: 10px !important; }
          .xs-step-sub  { font-size: 9px !important; }
          .xs-howto-text { font-size: 10px !important; }
          .xs-pad { padding: 10px !important; }
          .xs-py { padding-top: 8px !important; padding-bottom: 8px !important; }
        }
      `}</style>
    </div>
  );
}
